import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Collapse,
  IconButton,
  Button,
} from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInvoicesByUser } from "../../actions/invoiceActions";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    maximumFractionDigits: 0,
  }).format(v || 0);

const computeOutstanding = (invoice = {}) => {
  const total = Number(invoice.total ?? 0);
  const explicitReceived = Number(invoice.totalAmountReceived ?? 0);
  if (explicitReceived) return Math.max(total - explicitReceived, 0);
  const payments = Array.isArray(invoice.paymentRecords)
    ? invoice.paymentRecords.reduce(
        (sum, rec) => sum + Number(rec?.amountPaid || 0),
        0
      )
    : 0;
  return Math.max(total - payments, 0);
};

const isReceivedByToday = (invoice = {}) => {
  if (!invoice.receivedByDate) return false;
  const receivedDate = new Date(invoice.receivedByDate);
  if (Number.isNaN(receivedDate.getTime())) return false;
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  return receivedDate <= todayEnd;
};

export default function OutstandingInvoicesCard({
  title = "Outstanding Invoices",
  currency = "USD",
  autoLoad = true,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { invoices = [], isLoading = false } = useSelector(
    (state) => state?.invoices || {}
  );
  const [expandedCustomer, setExpandedCustomer] = React.useState(null);

  const userInfo = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return stored?.result || stored;
    } catch {
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (!autoLoad) return;
    const id = userInfo?._id || userInfo?.googleId;
    if (!id) return;
    if (invoices?.length) return;
    dispatch(getInvoicesByUser({ search: id }));
  }, [autoLoad, dispatch, invoices?.length, userInfo]);

  const outstandingByCustomer = React.useMemo(() => {
    const groups = new Map();
    (invoices || []).forEach((inv) => {
      if (!isReceivedByToday(inv)) return;
      const amountDue = computeOutstanding(inv);
      if (amountDue <= 0) return;
      const status = (inv?.status || "").toLowerCase();
      const isOutstanding =
        status === "unpaid" || status === "partial" || status === "pending";
      if (!isOutstanding) return;
      const customerKey =
        inv?.client?.store || inv?.client?.name || "Unknown customer";
      if (!groups.has(customerKey)) {
        groups.set(customerKey, {
          customer: customerKey,
          total: 0,
          invoices: [],
        });
      }
      groups.get(customerKey).total += amountDue;
      groups.get(customerKey).invoices.push({
        id: inv._id,
        invoiceNumber: inv.invoiceNumber,
        dueDate: inv.dueDate,
        receivedByDate: inv.receivedByDate,
        amountDue,
      });
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        invoices: group.invoices.sort(
          (a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0)
        ),
      }))
      .sort((a, b) => b.total - a.total);
  }, [invoices]);

  const totals = React.useMemo(() => {
    const customerCount = outstandingByCustomer.length;
    const invoiceCount = outstandingByCustomer.reduce(
      (sum, group) => sum + group.invoices.length,
      0
    );
    const overallTotal = outstandingByCustomer.reduce(
      (sum, group) => sum + group.total,
      0
    );
    return { customerCount, invoiceCount, overallTotal };
  }, [outstandingByCustomer]);

  const handleToggle = (customer) => {
    setExpandedCustomer((prev) => (prev === customer ? null : customer));
  };

  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardHeader
        title={title}
        subheader={
          outstandingByCustomer.length
            ? `${totals.customerCount} customer${
                totals.customerCount === 1 ? "" : "s"
              } • ${totals.invoiceCount} invoice${
                totals.invoiceCount === 1 ? "" : "s"
              }`
            : undefined
        }
        action={
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => history.push("/outstanding-invoices")}
            >
              View Page
            </Button>
            {totals.invoiceCount ? (
              <Box textAlign="right">
                <Typography variant="body2" color="textSecondary">
                  Total Outstanding
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {currencyFmt(totals.overallTotal, currency)}
                </Typography>
              </Box>
            ) : null}
            </Box>
        }
      />
      <CardContent style={{ maxHeight: 340, overflowY: "auto" }}>
        {isLoading ? (
          <Typography variant="body2" color="textSecondary">
            Loading invoices...
          </Typography>
        ) : outstandingByCustomer.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No outstanding balances.
          </Typography>
        ) : (
          outstandingByCustomer.map((group) => (
            <Box
              key={group.customer}
              borderBottom="1px solid rgba(0,0,0,0.06)"
              py={0.75}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {group.customer}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {group.invoices.length} invoice
                    {group.invoices.length === 1 ? "" : "s"} outstanding
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {currencyFmt(group.total, currency)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleToggle(group.customer)}
                    style={{
                      transform:
                        expandedCustomer === group.customer
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Collapse in={expandedCustomer === group.customer}>
                <Box mt={0.5}>
                  {group.invoices.map((inv) => (
                    <Box
                      key={inv.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={0.25}
                      pl={1}
                      style={{ cursor: "pointer" }}
                      onClick={() => history.push(`/invoice/${inv.id}`)}
                    >
                      <Box>
                        <Typography variant="body2">
                          Received By: {inv.receivedByDate
                            ? new Date(inv.receivedByDate).toLocaleDateString()
                            : inv.invoiceNumber || inv.id}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Due{" "}
                          {inv.dueDate
                            ? new Date(inv.dueDate).toLocaleDateString()
                            : "No due date"}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <Typography variant="body2">
                          {currencyFmt(inv.amountDue, currency)}
                        </Typography>
                        <LaunchIcon fontSize="small" color="primary" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
